import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAllJobApplication } from "./services/jobapplication.services";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import {
  clearFilterJobApplication,
  setFilterJobApplication,
} from "./jobapplicationSlice";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import IconComponent from "../../components/common/IconComponent";
import { FcDocument } from "react-icons/fc";
// import { Tooltip } from "@progress/kendo-react-tooltip";
// import ExpandableText from "../../components/common/ExpandableText";

const JobApplication: React.FC = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
    };
  }, []);
  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Job Application List
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <JobApplicationGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const JobApplicationGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const JobApplicationList = useAppSelector(
    (state) => state.jobapplication.JobApplicationList,
  );
  const loading = useAppSelector((state) => state.jobapplication.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterJobApplication = useAppSelector(
    (state) => state.jobapplication.FilterJobApplication,
  );
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  useEffect(() => {
    const payload = {
      ...FilterJobApplication,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllJobApplication(payload));
  }, [currentPage, pageLimit, FilterJobApplication]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 3 || props.index === 4 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const JobApplicationFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.jobapplication.FilterJobApplication}
        setFilterAction={setFilterJobApplication}
        clearFilterAction={clearFilterJobApplication}
        fetchDataAction={getAllJobApplication}
        pagination={{ case: "capitalize" }}
      />
    ),
    [],
  );

  // if (loading) return ;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        className="responsive-table"
        filter={filter}
        data={
          filter ? filterBy(JobApplicationList, filter) : JobApplicationList
        }
        onFilterChange={handleFilterChange}
        total={JobApplicationList[0]?.itemcount || JobApplicationList.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="JobTitle"
          title="Job Title"
          width={200}
          cells={{
            data: (props) => (
              <td>
                <div>{props?.dataItem.JobTitle}</div>
              </td>
            ),
            filterCell: JobApplicationFilterCell,
          }}
        />
        <Column
          field="FullName"
          title="Full Name"
          width={200}
          cells={{
            data: (props) => (
              <td>
                <div>{props?.dataItem.FullName}</div>
              </td>
            ),
            filterCell: JobApplicationFilterCell,
          }}
        />
        <Column
          field="EmailID"
          title="Email ID"
          width={280}
          cells={{
            data: (props) => (
              <td>
                <div>{props?.dataItem.EmailID}</div>
              </td>
            ),
            filterCell: JobApplicationFilterCell,
          }}
        />
        <Column
          field="MobileNo"
          title="Mobile No"
          width={150}
          cells={{
            data: (props) => (
              <td>
                <div>{props?.dataItem.MobileNo}</div>
              </td>
            ),
            filterCell: JobApplicationFilterCell,
          }}
        />
        <Column
          field="PositionTitle"
          title="Position"
          width={150}
          cells={{
            data: (props) => (
              <td>
                <div>{props?.dataItem.PositionTitle}</div>
              </td>
            ),
            filterCell: JobApplicationFilterCell,
          }}
        />
        <Column
          field="TotalExperience"
          title="Experience"
          width={150}
          filterable={false}
        />
        <Column
          field="CurrentCompany"
          title="Current Company"
          width={200}
          filterable={false}
        />
        <Column
          field="CurrentSalary"
          title="Current Salary"
          width={150}
          filterable={false}
        />
        <Column
          field="ExpectedSalary"
          title="Expected Salary"
          width={150}
          filterable={false}
        />
        <Column
          field="NoticePeriod"
          title="Notice Period"
          width={150}
          filterable={false}
        />
        <Column
          field="ResumeUpload"
          title="Resume"
          width={180}
          filterable={false}
          cells={{
            data: (props) => (
              <td>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {props?.dataItem.ResumeUpload ? (
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={() => window.open(props?.dataItem.ResumeUpload)}
                    >
                      <IconComponent
                        icon={FcDocument}
                        style={{ fontSize: 24 }}
                      />
                    </div>
                  ) : (
                    "No"
                  )}
                </div>
              </td>
            ),
          }}
        />
      </KendoGrid>
    </>
  );
};

export default JobApplication;
